<template>
  <div>
    <popup-hint hint="items">
      <template v-slot:breadcrumbs>
        <breadcrumbs :home="$t('title')">
          <template v-slot:left v-if="lowInventoryCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{lowInventoryCount}} item{{lowInventoryCount>1?'s':''}} with low inventory
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.categoryId=null;filters.siteId=null;filters.status='1';filters.lowInventory='1';filters.asof=today})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Items are all non-alcohol inventory you use, such as
        raw materials, like grain for mashing or bottles for bottling.
      </template>
      <template v-slot:bottom>
        You can create items individually or do a batch import of items.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'InventoryItemsEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="items" :fields="importFields" importAction="itemsImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="items" objectType="items" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="items2" v-model="selected" @list="listBuilt"
                   fields="id,type,name,active,location,sku,trackUnits,precision,reorderPoint,buyingUnits,buyingPrecision,
                            pictureURL,preferredVendor{id},lotTracking,assembled,
                            quantity,value,lowInventory,
                            category{id,name},sold,
                            onOrder,onSalesOrder,sites{siteId,lot,quantity,value}"
                   totalFields="totalValue"
                   :totals.sync="totals"
                   :listFilters="listFilters" :parameters="listParameters"
                   @dblclick="item => $router.push({name:'InventoryItemsTransactions',params:{itemId:item.id},query:{siteId:filters.siteId}})"
                   :item-class="item => (item.active ? '' : 'grey--text')"
                   title="Items"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[`As of ${$formatDate(filters.asof)}`,
                                          filters.lowInventory=='1'?'Low inventory only':'',
                                          filters.sold=='1'?'Sold items only':'',
                                          filters.categoryId!=null?categoryName:'',
                                          filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                          filters.siteId!=null?siteName:'']}"
                   download-endpoint="itemsExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.categoryId=null;filters.siteId=null;filters.status='1';filters.lowInventory='0';filters.sold='0';filters.type='0';filters.asof=today;filters.search='';filters.id=null;filters.upc=''">
          <v-list-item>
            <filter-search :label="[{text:'Name',value:'name'},{text:'SKU',value:'sku'},{text:'Lot',value:'lot'}]" v-model="filters.search" :search-field.sync="filters.searchField" :search-display.sync="searchDisplay"/>
          </v-list-item>
          <v-list-item>
            <v-checkbox v-model="filters.lowInventory" true-value="1" false-value="0" label="Low inventory only" class="mt-n4"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.sold" true-value="1" false-value="0" label="Sold items only"/>
          </v-list-item>
          <v-list-item>
            <inventory-categories-select v-model="filters.categoryId" label="Category" type="Item" :name.sync="categoryName" :category-list.sync="categories" none-text="All"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Type" dense outlined :items="[{text:'All',value:'0'},{divider:true},{text:'Items',value:'1'},{text:'Non-inventory items',value:'2'},{text:'Services',value:'3'}]" v-model="filters.type"/>
          </v-list-item>
          <v-list-item>
            <date-picker label="As of" v-model="filters.asof" date-only/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id||filters.upc" close small color="primary" class="ml-1" @click:close="filters.id=null;filters.upc=''">Scanned item</v-chip>
          <v-chip v-show="filters.asof!==today" close small class="ml-1" @click:close="filters.asof=today">As of {{filters.asof | formatDate}}</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">{{searchDisplay}}</v-chip>
          <v-chip v-show="filters.lowInventory=='1'" close small class="ml-1" @click:close="filters.lowInventory='0'">Low inventory only</v-chip>
          <v-chip v-show="filters.sold=='1'" close small class="ml-1" @click:close="filters.sold='0'">Sold items only</v-chip>
          <v-chip v-show="filters.categoryId!=null" close small class="ml-1" @click:close="filters.categoryId=null">{{categoryName}}</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.type!='0'" close small class="ml-1" @click:close="filters.type='0'">{{filters.type=='1'?'Items':filters.type=='2'?'Non-inventory items':'Services'}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'InventoryItemsEditNew'}"><v-list-item-title>Create item</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'InventoryItemCategories'}"><v-list-item-title>Categories</v-list-item-title></v-list-item>
        <v-menu :nudge-left="220" v-if="!$vuetify.breakpoint.mobile">
          <template v-slot:activator="{on:reports}">
            <v-list-item v-on="reports">
              <v-list-item-title>Reports</v-list-item-title>
              <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Items and services',to:{name:'InventoryItems'}}]},query:{to:'reports/inventory/ReportsItemsActivity'}}"><v-list-item-title>Items activity</v-list-item-title></v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Items and services',to:{name:'InventoryItems'}}]},query:{to:'reports/inventory/ReportsItemsPeriodSummary'}}"><v-list-item-title>Items period summary</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-divider/>
          <v-list-item @click="doImport=true"><v-list-item-title>Import items from CSV</v-list-item-title></v-list-item>
        </template>
        <template v-if="$store.getters.company.connectedToAccounting">
          <v-divider/>
          <v-list-item @click="sync"><v-list-item-title>Sync items with QBO</v-list-item-title></v-list-item>
        </template>
        <v-divider/>
        <v-list-item v-if="sites.length>1" :to="{name:'InventoryItemsBatchMove'}"><v-list-item-title>Batch item move</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Items and services',to:{name:'InventoryItems'}}]},query:{to:'reports/inventory/ReportsInventoryItemsPhysical'}}"><v-list-item-title>Physical inventory worksheet</v-list-item-title></v-list-item>
        <v-list-item :to="{name:'InventoryItemsReconcile'}"><v-list-item-title>Reconcile item inventory</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <inventory-items-batch-edit :items="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'InventoryItemsEdit',params:{itemId:item.id}})"
               :class="{'text-decoration-underline':hover}">{{item.name}}</a>
          </v-hover>
          <template v-else>{{item.name}}</template>
          <div v-if="!item.active" class="grey--text text-caption">Inactive</div>
          <div v-else-if="item.type=='NonTracking'" class="text-caption">(Not tracked)</div>
          <div v-else-if="item.type=='Service'" class="text-caption">Service</div>
        </template>
      </template>
      <template v-slot:item.category.name="{item}">
        {{item.category ? item.category.name : ''}}
      </template>
      <template v-slot:item.available="{item,printing}">
        <template v-if="item.type==='Item'">
          <template v-if="printing">
            {{item.quantity-item.onSalesOrder | number(item.precision)}} {{item.trackUnits}}
          </template>
          <template v-else>
            <v-tooltip bottom :disabled="!item.onSalesOrder">
              <template v-slot:activator="{on}">
                <div :class="{'warning--text':item.lowInventory}" v-on="on">
                  {{item.quantity-item.onSalesOrder | number(item.precision)}} {{item.trackUnits}}
                </div>
              </template>
              {{item.quantity | number(item.precision)}} {{item.trackUnits}} on hand -
              {{item.onSalesOrder | number(item.precision)}} {{item.trackUnits}} on sales order
            </v-tooltip>
            <div v-if="item.assembled && item.lowInventory"><v-btn rounded color="warning lighten-1" x-small :to="{name:'ProductionAssemblyEditNew',params:{itemId:item.id}}">Assemble</v-btn></div>
            <div v-else-if="item.lowInventory"><v-btn rounded color="warning lighten-1" x-small :to="{name:'PurchasingPurchaseOrdersEditNew',params:{itemType:'Item',itemId:item.id}}">Create PO</v-btn></div>
          </template>
        </template>
      </template>
      <template v-slot:item.onHand="{item,printing}">
        <template v-if="item.type==='Item'">
          <template v-if="printing">
            {{item.quantity | number(item.precision)}} {{item.trackUnits}}
          </template>
          <template v-else>
            <span :class="{'warning--text':item.lowInventory}">
              {{item.quantity | number(item.precision)}} {{item.trackUnits}}
            </span>
            <div v-if="item.assembled && item.lowInventory"><v-btn rounded color="warning lighten-1" x-small :to="{name:'ProductionAssemblyEditNew',params:{itemId:item.id}}">Assemble</v-btn></div>
            <div v-else-if="item.lowInventory"><v-btn rounded color="warning lighten-1" x-small :to="{name:'PurchasingPurchaseOrdersEditNew',params:{itemType:'Item',itemId:item.id}}">Create PO</v-btn></div>
          </template>
        </template>
      </template>
      <template v-slot:item.onOrder="{item}">
        <template v-if="item.type==='Item' && item.onOrder">
          {{item.onOrder | number(item.buyingPrecision)}} {{item.buyingUnits}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:item.onSalesOrder="{item}">
        <template v-if="item.type==='Item' && item.onSalesOrder">
          {{item.onSalesOrder | number(item.precision)}} {{item.trackUnits}}
        </template>
        <template v-else>{{null}}</template>
      </template>
      <template v-slot:item.value="{item}">
        {{item.type==='Item' ? item.value : '' | currency}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'InventoryItemsEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <inventory-item-menu :item="item" :readOnly="readOnly" :siteId="filters.siteId"
                              @delete="itemObject=item;showDelete=true"
                              @updateFromAccountinng="itemObject=item;showUpdateFromAccounting=true"/>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:item.sold="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.unitCost="{item}">
        {{item.type==='Item' ? (item.quantity ? item.value/item.quantity : 0) : '' | currency}}
      </template>
      <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
        <v-icon v-if="item.type==='Item'" @click="expand(!isExpanded)">{{isExpanded?'mdi-chevron-up':'mdi-chevron-down'}}</v-icon>
      </template>
      <template v-slot:expanded-item="{ item, headers }">
        <td :colspan="headers.length">
          <v-card class="my-4 text-caption" :class="expansionPanelClass">
            <v-simple-table dense class="my-2 pl-12" :class="expansionPanelClass" style="width:60%">
              <thead>
                <tr>
                  <th v-if="!filters.siteId && sites.length>1"  :class="expansionPanelClass" width="40%">Site</th>
                  <th class="py-0" :class="expansionPanelClass" width="20%">Lot</th>
                  <th class="py-0 text-right" :class="expansionPanelClass" width="20%">Quantity</th>
                  <th class="py-0 text-right" :class="expansionPanelClass" width="20%" v-if="values">Value</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line,i) of itemInventory(item)" :key="i">
                  <td v-if="!filters.siteId && sites.length>1" class="text-caption">
                    {{sites.find(site => site.id === line.siteId).name}}
                  </td>
                  <td class="text-caption py-0">
                    <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Items',to:{name:'InventoryItems'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:line.lot,forward:1}})">{{line.lot}}</a>
                  </td>
                  <td class="text-caption py-0 text-right">{{line.quantity | number(item.precision)}} {{item.trackUnits}}</td>
                  <td class="text-caption py-0 text-right" v-if="values">{{line.value | currency}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="itemObject.name" @delete="performDeleteItem"/>
    <update-object-from-accounting v-model="showUpdateFromAccounting" :name="itemObject.name" @update="performUpdateItemFromAccounting"/>
  </div>
</template>

<script>
import InventoryItemMenu from '@/views/inventory/items/InventoryItemMenu'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'
import InventoryItemsBatchEdit from '@/views/inventory/items/InventoryItemsBatchEdit'
import {format} from 'date-fns'

export default {
  name: 'InventoryItems',
  components: {InventoryItemMenu,InventoryCategoriesSelect,InventoryItemsBatchEdit},
  data() {
    return {
      filters: {
        lowInventory: '0',
        sold: '0',
        asof: format(new Date(), 'yyyy-MM-dd'),
        status: '1',
        siteId: null,
        categoryId: null,
        type: '0',
        search: '',
        searchField: 'name',
        id: null,
        upc: ''
      },
      searchDisplay: '',
      categories: [],
      categoryName: '',
      lowInventoryCount: 0,
      showStatus: false,
      today: format(new Date(), 'yyyy-MM-dd'),
      totals: {totalValue: 0},
      itemObject: {},
      showDelete: false,
      doImport: false,
      showUpdateFromAccounting: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'SKU',mapTo:'',mandatory:false},
                     {name:'Type',mapTo:'',mandatory:false},{name:'Track lot',mapTo:'',mandatory:false},
                     {name:'Units',mapTo:'',mandatory:false},
                     {name:'Default price',mapTo:'',mandatory:false},
                     {name:'Initial quantity',mapTo:'',mandatory:false},{name:'Initial value',mapTo:'',mandatory:false},
                     {name:'Initial lot',mapTo:'',mandatory:false},{name:'Initial date',mapTo:'',mandatory:false,date:true},
                     {name:'Category',mapTo:'',mandatory:false}],
      instructions: `<ol class="mb-4">
                      <li>Name</li>
                      <li>SKU (optional)</li>
                      <li>Type (Item, Non-tracking, or Service) (optional, default is Item)</li>
                      <li>Track lot (1 or 0) (optional, default is 1)</li>
                      <li>Tracking units (optional, default is "items")</li>
                      <li>Default price (optional, default is 0)</li>
                      <li>Initial quantity (optional, default is 0)</li>
                      <li>Total initial value (optional, default is 0)</li>
                      <li>Initial lot (optional, default is L0)</li>
                      <li>Initial date (optional, default is today)</li>
                      <li>Category (optional)</li>
                      </ol>
                      <em>Note that not all information for an item can be imported.</em>
                      You should, therefore, check each imported item to ensure all settings are correct.`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'sku', visible:true},
                       { value: 'category.name', visible:false},
                       { value: 'available', visible:true},
                       { value: 'onHand', visible:true},
                       { value: 'onOrder', visible:true},
                       { value: 'onSalesOrder', visible:false},
                       { value: 'value', visible:true},
                       { value: 'unitCost', visible:false},
                       { value: 'sold', visible:false},
                       { value: 'location', visible:false}],
      headers: [{ text: this.$t('header.name'), value: 'name', width:'35%',sortable:true},
                { text: this.$t('header.sku'), value: 'sku', width:'10%',sortable:true},
                { text: this.$t('header.category'), value: 'category.name', width:'20%',sortable:true},
                { text: this.$t('header.available'), value: 'available', width:'15%',sortable:false,align:'right'},
                { text: this.$t('header.onHand'), value: 'onHand', width:'15%',sortable:false,align:'right'},
                { text: this.$t('header.onPO'), value: 'onOrder', width:'15%',sortable:false,align:'right'},
                { text: this.$t('header.onSO'), value: 'onSalesOrder', width:'15%',sortable:false,align:'right'},
                { text: this.$t('header.value'), value: 'value', width:'15%',sortable:true,align:'right', omit:() => !this.values},
                { text: this.$t('header.unitCost'), value:'unitCost', width:'15%',sortable:false,align:'right'},
                { text: this.$t('header.sold'), value: 'sold', width:'10%',sortable:true,align:'center'},
                { text: this.$t('header.location'), value: 'location', width:'15%',sortable:false}]
    }
  },
  computed: {
    sites() {
      return this.$store.getters.sites(true);
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    connectedToAccounting() {
      return this.$store.getters.company.connectedToAccounting;
    },
    readOnly() {
      return !this.$store.getters.company.permissions.includes('inventory.write');
    },
    values() {
      return this.$store.getters.company.permissions.includes('values');
    },
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.upc)
        filters.push({field:'upc', comparator:'eq', value:this.filters.upc});
      if (this.filters.lowInventory == '1')
        filters.push({field:'lowInventory', comparator:'eq', value:'true'});
      if (this.filters.sold == '1')
        filters.push({field:'sold', comparator:'eq', value:'true'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});
      if (this.filters.categoryId)
        filters.push({field:'categoryId', comparator:'in', value:this.filters.categoryId});
      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});
      if (this.filters.search)
        filters.push({field:this.filters.searchField, comparator:'contains', value:this.filters.search});
      if (this.filters.type !== '0')
        filters.push({field:'type', comparator:'eq', value:{'1':'Item','2':'NonTracking','3':'Service'}[this.filters.type]});

      return filters;
    },
    listParameters() {
      return this.filters.asof ? [{name:'asof', value:`${this.filters.asof} 23:59:59`}] : [];
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');

    const results = await this.$apiCall(`query InventoryItems {
                                            itemsLowInventory
                                          }`,
                                      {});
    if (!results.error)
      this.lowInventoryCount = results.data.itemsLowInventory;

    if (!['InventoryItemsEdit', 'InventoryItemsTransactions','InventoryItemCategories','InventoryItemsReconcile'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.lowInventoryCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    scan({type, id, code}) {
      if (type === 'Item' || code)
        Object.assign(this.filters, {categoryId:null,siteId:null,status:'2',lowInventory:'0',sold:'0',type:'0',asof:this.today,search:'',id:null,upc:''});
      if (type === 'Item')
        this.filters.id = id;
      else if (code)
        this.filters.upc = code;
    },
    listBuilt({list}) {
      if ((this.filters.id || this.filters.upc) && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    itemInventory(item) {
      if (!this.filters.siteId)
        return item.sites;
      const result = item.sites.reduce((result, site) => {
        if (!this.filters.siteId || site.siteId === this.filters.siteId) {    // Site is okay
          if (result[site.lot]) {
            result[site.lot].quantity += site.quantity;
            result[site.lot].value += site.value;
          }
          else
            result[site.lot] = {...site};
        }
        return result;
      }, {});
      return Object.values(result);
    },
    itemType(item) {
      if (item.type === 'NonTracking')
        return 'Item (not tracked)';
      return item.type;
    },
    async performDeleteItem() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryItems($id: ID!) {
                                             objectDelete(id:$id)
                                            }`,
                                          {id:this.itemObject.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table?.buildList();
        this.$store.commit('itemDelete', this.itemObject.id);
        this.$store.commit('setError', {type:'success',message:`Deleted item ${this.itemObject.name}`});
      }
    },
    async performUpdateItemFromAccounting() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryItems($id: ID!) {
                                                itemUpdateFromAccounting(id:$id) {
                                                  id,name,sku,active
                                                }
                                              }`,
                                          {id:this.itemObject.id});
      this.loading = false;
      if (!results.error) {
        this.showUpdateFromAccounting = false;
        this.$set(this.itemObject, 'name', results.data.itemUpdateFromAccounting.name);
        this.$set(this.itemObject, 'sku', results.data.itemUpdateFromAccounting.sku);
        this.$set(this.itemObject, 'active', results.data.itemUpdateFromAccounting.active);
      }
    },
    async sync() {
      this.loading = true;
      const results = await this.$apiCall(`mutation InventoryItems {
                                             accountingIntegrationSync(step:"items") {name}
                                           }`, {});
      this.$refs.table?.buildList();
      if (!results.error)
        this.$store.commit('setError', {type:'success',message:`Synced items with QBO`});
    }
  },

  i18n: {
    messages: {
      en: {
        title: 'Items and services',
        header: {
          name: 'ITEM',
          sku: 'SKU',
          category: 'CATEGORY',
          available: 'AVAILABLE',
          onHand: 'ON HAND',
          onPO: 'ON PO',
          onSO: 'ON SO',
          value: 'VALUE',
          unitCost: 'UNIT COST',
          sold: 'SOLD',
          location: 'LOCATION'
        }
      },
      fr: {
        title: 'Articles et services',
        header: {
          name: 'NOM',
          sku: 'SKU',
          category: 'CATÉGORIE',
          available: 'DISPONIBLE',
          onHand: 'COMPTER',
          onPO: 'PO',
          onSO: 'SO',
          value: 'VALEUR',
          unitCost: 'COÛT',
          sold: 'VENDUE',
          location: 'EMPLACEMENT'
        }
      }
    }
  }
}
</script>